<template>
  <div class="request-mail">
    <validation-observer class="validation-observer" v-slot="{ invalid }">
      <div class="input-area">
        <div class="row mail-text">
          <div class="label">予約申請完了時の送信メール</div>
          <div class="field">
            <div class="content subject">
              <div class="title">件名</div>
              <div class="text">予約申請完了のお知らせ（返信不可）</div>
            </div>
            <div class="content body">
              <div class="title">本文</div>
              <div class="text">
                <div class="upper-text">{{ mixinMailTextAboveInput }}</div>
                <div class="input-text">
                  <validation-provider
                    class="validation-provider"
                    :rules="{ notSurrogatePair: true }"
                    v-slot="{ errors }"
                  >
                    <base-multiple-lines-text-box
                      class="text-form"
                      :styles="textareaStyle"
                      :wrap="'off'"
                      v-model="editData.requestMail"
                    />
                    <div class="error">{{ errors[0] }}</div>
                  </validation-provider>
                </div>
                <div class="under-text">{{ mixinMailTextBelowInput }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="enAvailable" class="row mail-text">
          <div class="label">予約申請完了時の送信メール（英語）</div>
          <div class="field">
            <div class="content subject">
              <div class="title">件名</div>
              <div class="text">
                <span>{{
                  'Your reservation request has been received (no reply)'
                }}</span>
              </div>
            </div>
            <div class="content body">
              <div class="title">本文</div>
              <div class="text">
                <div class="upper-text">{{ mixinMailTextAboveInputEn }}</div>
                <div class="input-text">
                  <validation-provider
                    class="validation-provider"
                    :rules="{ notSurrogatePair: true }"
                    v-slot="{ errors }"
                  >
                    <base-multiple-lines-text-box
                      class="text-form"
                      :styles="textareaStyle"
                      :wrap="'off'"
                      v-model="editData.requestMailEnglish"
                    />
                    <div class="error">{{ errors[0] }}</div>
                  </validation-provider>
                </div>
                <div class="under-text">{{ mixinMailTextBelowInputEn }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="button-area">
        <base-button-medium-orange
          class="button"
          v-if="lookOnlyFlg === 0"
          :disabled="invalid || waitFlg"
          @click="updateData"
          >登録</base-button-medium-orange
        >
      </div>
      <announce-popup
        v-if="alertFlg"
        :type="type"
        :title="title"
        :buttons="buttons"
        @close="closeAlert"
        >{{ popupMessage }}</announce-popup
      >
      <unsaved-leave-popup />
    </validation-observer>
  </div>
</template>

<script>
import BaseMultipleLinesTextBox from '@/components/parts/atoms/BaseMultipleLinesTextBox.vue'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import MailTemplate from '@/components/mixins/MailTemplate'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import '@/utils/validation_rules'
import { mapGetters } from 'vuex'
import dedent from 'dedent'
import {
  mailTextReservationInformation,
  mailTextReservationInformationEn
} from '@/utils/mail_text'

export default {
  name: 'RequestMail',

  components: {
    BaseMultipleLinesTextBox,
    BaseButtonMediumOrange,
    AnnouncePopup,
    UnsavedLeavePopup,
    ValidationObserver,
    ValidationProvider
  },

  mixins: [MailTemplate, CheckInputDifference],

  data() {
    return {
      editData: {},
      enAvailable: false,
      textareaStyle: {
        width: '500px',
        height: 'auto',
        minHeight: '150px',
        fontSize: '13px',
        resize: 'both'
      },
      waitFlg: false,
      alertFlg: false,
      type: '',
      title: '',
      popupMessage: '',
      buttons: ['閉じる']
    }
  },

  computed: {
    ...mapGetters({
      lookOnlyFlg: 'auth/lookOnlyFlg'
    })
  },

  created() {
    this.setData()
  },

  methods: {
    setData() {
      const reservationSetting = this.$store.getters[
        'reservationSetting/getData'
      ]

      // 入力内容の初期化
      this.editData = {
        id: reservationSetting.id,
        requestMail: reservationSetting.requestMail,
        requestMailEnglish: reservationSetting.requestMailEnglish
      }
      this.mixinInputData = this.editData
      this.mixinSetInitialData()

      // 英語表記の設定を反映
      this.enAvailable = reservationSetting.englishOptionFlg === 1

      // メール固定文を設定
      this.mixinSetMailTexts()
      this.mixinMailTextAboveInput = dedent`
        ${this.mixinClinicName}をご利用いただきありがとうございます。
        以下の内容で予約申請を受け付けました。
        予約登録完了後、確認メールを送らせていただきますので暫くお待ちください。

        ${mailTextReservationInformation()}`
      this.mixinMailTextAboveInputEn = dedent`
        Thank you for making a reservation at ${this.mixinClinicNameEn}.
        Your reservation request with the details below was submitted.
        An email will be automatically sent to you once the reservation is confirmed.

        ${mailTextReservationInformationEn()}`
    },
    async updateData() {
      this.waitFlg = true
      const res = await this.$store.dispatch(
        'reservationSetting/updateMailText',
        this.editData
      )
      if (res === true) {
        this.type = 'success'
        this.title = '完了'
        this.popupMessage = '編集しました'
        this.setData()
      } else {
        this.type = 'failure'
        this.title = '失敗'
        this.popupMessage = '編集に失敗しました'
      }
      this.waitFlg = false
      this.alertFlg = true
    },
    closeAlert() {
      this.alertFlg = false
    }
  }
}
</script>

<style lang="scss" scoped>
.request-mail {
  @include delivery-component-style($label-min-width: 256px);
}
</style>
